import { useEffect, useRef, useState } from "react"
import { useEdgeApi } from "../edge/EdgeProvider"

export interface SignalStrengthPoint {
  timestamp: number
  signalStrength: number
}

export const HISTORY_DURATION_MS = 60 * 1000
export const SAMPLE_INTERVAL_MS = 1000
/** What the modem reports before the first measurement and whenever the signal is lost. */
export const NO_SIGNAL_STRENGTH = -10

/**
 * Samples kept older than the visible window. A rolling path update may only
 * translate the retained window, which requires the oldest retained sample to
 * sit outside the plot area once the path is shifted. Two samples of overscan
 * also absorb a doubled tick after the timer is throttled.
 */
const OVERSCAN_SAMPLES = 2
const VISIBLE_SAMPLE_COUNT = HISTORY_DURATION_MS / SAMPLE_INTERVAL_MS + 1
const SAMPLE_COUNT = VISIBLE_SAMPLE_COUNT + OVERSCAN_SAMPLES

const createInitialHistory = (): SignalStrengthPoint[] => {
  const timestamp = Date.now()

  return Array.from({ length: SAMPLE_COUNT }, (_, index) => ({
    timestamp: timestamp - (SAMPLE_COUNT - 1 - index) * SAMPLE_INTERVAL_MS,
    signalStrength: NO_SIGNAL_STRENGTH,
  }))
}

/**
 * The window the chart shows, ending at the newest sample. The overscan samples
 * fall outside it and are clipped away.
 */
export const visibleTimeDomain = (points: readonly SignalStrengthPoint[]): readonly [number, number] | undefined => {
  const newestTimestamp = points[points.length - 1]?.timestamp

  if (newestTimestamp === undefined) {
    return undefined
  }

  return [newestTimestamp - HISTORY_DURATION_MS, newestTimestamp]
}

const useSignalStrengthHistory = (): SignalStrengthPoint[] => {
  const { metrics } = useEdgeApi()
  const signalStrength = metrics.useModemSignalStrength()
  const [history, setHistory] = useState<SignalStrengthPoint[]>(createInitialHistory)
  const latestSignalStrength = useRef(signalStrength ?? NO_SIGNAL_STRENGTH)

  useEffect(() => {
    if (signalStrength !== undefined) {
      latestSignalStrength.current = signalStrength
    }
  }, [signalStrength])

  useEffect(() => {
    const interval = setInterval(() => {
      setHistory((previousHistory) => {
        const previousPoint = previousHistory[previousHistory.length - 1]
        const timestamp = (previousPoint?.timestamp ?? Date.now()) + SAMPLE_INTERVAL_MS

        return [...previousHistory.slice(1), { timestamp, signalStrength: latestSignalStrength.current }]
      })
    }, SAMPLE_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [])

  return history
}

export default useSignalStrengthHistory
